import { parseISO, isBefore, isAfter, startOfDay, addDays } from 'date-fns';

export type CityDateIssueType = 'outside_trip_range' | 'overlap';

export interface CityDateIssue {
  type: CityDateIssueType;
  /** For overlap issues, the IDs of cities this city overlaps with */
  overlappingCityIds?: number[];
  /** For overlap issues, the names of cities this city overlaps with */
  overlappingCityNames?: string[];
}

export interface CityDateValidationResult {
  cityId: number;
  cityName: string;
  issues: CityDateIssue[];
  hasArrivalIssue: boolean;
  hasDepartureIssue: boolean;
}

export interface CityForValidation {
  id: number;
  cityName: string;
  arrivalDate: string; // ISO date string
  departureDate: string; // ISO date string
}

/**
 * Checks if a city's dates are outside the trip date range
 */
export function isCityOutsideTripRange(
  city: CityForValidation,
  tripStartDate: string,
  tripEndDate: string
): { isOutside: boolean; arrivalOutside: boolean; departureOutside: boolean } {
  const cityArrival = startOfDay(parseISO(city.arrivalDate));
  const cityDeparture = startOfDay(parseISO(city.departureDate));
  const tripStart = startOfDay(parseISO(tripStartDate));
  const tripEnd = startOfDay(parseISO(tripEndDate));

  const arrivalOutside = isBefore(cityArrival, tripStart) || isAfter(cityArrival, tripEnd);
  const departureOutside = isBefore(cityDeparture, tripStart) || isAfter(cityDeparture, tripEnd);

  return {
    isOutside: arrivalOutside || departureOutside,
    arrivalOutside,
    departureOutside,
  };
}

/**
 * Checks if two cities have overlapping date ranges
 */
export function doCitiesOverlap(cityA: CityForValidation, cityB: CityForValidation): boolean {
  const aArrival = startOfDay(parseISO(cityA.arrivalDate));
  const aDeparture = startOfDay(parseISO(cityA.departureDate));
  const bArrival = startOfDay(parseISO(cityB.arrivalDate));
  const bDeparture = startOfDay(parseISO(cityB.departureDate));

  // Two ranges overlap if neither is completely before or after the other
  const aEndsBeforeBStarts = isBefore(aDeparture, bArrival);
  const aStartsAfterBEnds = isAfter(aArrival, bDeparture);

  return !(aEndsBeforeBStarts || aStartsAfterBEnds);
}

/**
 * Find all cities that overlap with a given city
 */
export function findOverlappingCities(
  city: CityForValidation,
  allCities: CityForValidation[]
): CityForValidation[] {
  return allCities.filter((other) => other.id !== city.id && doCitiesOverlap(city, other));
}

/**
 * Validates all cities against trip dates and each other for overlaps
 * Returns validation results for cities that have issues
 */
export function validateCityDates(
  cities: CityForValidation[],
  tripStartDate: string,
  tripEndDate: string
): CityDateValidationResult[] {
  const results: CityDateValidationResult[] = [];

  for (const city of cities) {
    const issues: CityDateIssue[] = [];
    let hasArrivalIssue = false;
    let hasDepartureIssue = false;

    // Check if outside trip range
    const rangeCheck = isCityOutsideTripRange(city, tripStartDate, tripEndDate);
    if (rangeCheck.isOutside) {
      issues.push({ type: 'outside_trip_range' });
      hasArrivalIssue = hasArrivalIssue || rangeCheck.arrivalOutside;
      hasDepartureIssue = hasDepartureIssue || rangeCheck.departureOutside;
    }

    // Check for overlaps with other cities
    const overlapping = findOverlappingCities(city, cities);
    if (overlapping.length > 0) {
      issues.push({
        type: 'overlap',
        overlappingCityIds: overlapping.map((c) => c.id),
        overlappingCityNames: overlapping.map((c) => c.cityName),
      });
      // Both dates are involved in an overlap
      hasArrivalIssue = true;
      hasDepartureIssue = true;
    }

    if (issues.length > 0) {
      results.push({
        cityId: city.id,
        cityName: city.cityName,
        issues,
        hasArrivalIssue,
        hasDepartureIssue,
      });
    }
  }

  return results;
}

/**
 * Creates a Set of city IDs that have date validation issues
 */
export function getCitiesWithDateIssues(
  cities: CityForValidation[],
  tripStartDate: string,
  tripEndDate: string
): Set<number> {
  const validationResults = validateCityDates(cities, tripStartDate, tripEndDate);
  return new Set(validationResults.map((r) => r.cityId));
}

/**
 * Gets validation result for a specific city
 */
export function getCityValidationResult(
  cityId: number,
  cities: CityForValidation[],
  tripStartDate: string,
  tripEndDate: string
): CityDateValidationResult | null {
  const results = validateCityDates(cities, tripStartDate, tripEndDate);
  return results.find((r) => r.cityId === cityId) ?? null;
}

export interface DateGap {
  start: Date;
  end: Date;
}

/**
 * Checks if cities fully cover the trip date range (no gaps)
 * Returns uncovered date ranges if there are gaps
 */
export function findUncoveredDateRanges(
  cities: CityForValidation[],
  tripStartDate: string,
  tripEndDate: string
): DateGap[] {
  if (cities.length === 0) {
    const tripStart = startOfDay(parseISO(tripStartDate));
    const tripEnd = startOfDay(parseISO(tripEndDate));
    return [{ start: tripStart, end: tripEnd }];
  }

  const tripStart = startOfDay(parseISO(tripStartDate));
  const tripEnd = startOfDay(parseISO(tripEndDate));

  // Sort cities by arrival date
  const sortedCities = [...cities].sort((a, b) => {
    const aDate = parseISO(a.arrivalDate);
    const bDate = parseISO(b.arrivalDate);
    return aDate.getTime() - bDate.getTime();
  });

  const gaps: DateGap[] = [];

  // Check for gap at the start
  const firstCityArrival = startOfDay(parseISO(sortedCities[0].arrivalDate));
  if (isBefore(tripStart, firstCityArrival)) {
    gaps.push({
      start: tripStart,
      end: addDays(firstCityArrival, -1),
    });
  }

  // Check for gaps between cities
  for (let i = 0; i < sortedCities.length - 1; i++) {
    const currentCityDeparture = startOfDay(parseISO(sortedCities[i].departureDate));
    const nextCityArrival = startOfDay(parseISO(sortedCities[i + 1].arrivalDate));

    // There's a gap if next city starts more than 1 day after current city ends
    const expectedNextDay = addDays(currentCityDeparture, 1);
    if (isBefore(expectedNextDay, nextCityArrival)) {
      gaps.push({
        start: expectedNextDay,
        end: addDays(nextCityArrival, -1),
      });
    }
  }

  // Check for gap at the end
  const lastCityDeparture = startOfDay(
    parseISO(sortedCities[sortedCities.length - 1].departureDate)
  );
  if (isBefore(lastCityDeparture, tripEnd)) {
    gaps.push({
      start: addDays(lastCityDeparture, 1),
      end: tripEnd,
    });
  }

  return gaps;
}

/**
 * Checks if cities fully cover the trip date range
 */
export function isTripFullyCoveredByCities(
  cities: CityForValidation[],
  tripStartDate: string,
  tripEndDate: string
): boolean {
  const gaps = findUncoveredDateRanges(cities, tripStartDate, tripEndDate);
  return gaps.length === 0;
}
